/** @jsxImportSource @emotion/react */
import { UIEventHandler, useContext, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Form, Input, Popconfirm } from 'antd';
import { useForm } from 'antd/lib/form/Form';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import { documents, documentsQueries } from 'api';
import { ThemeContext, UserContext } from 'contexts';
import { Campaigns, Edit, TrashFull } from 'icons';
import { dateSpecialFormat, formErrorHandler, format, timeSpecialFormat } from 'utils';
import { APIDocument, DocumentComment, DocumentCommentSource } from 'types';
import { stylesInit } from './DocumentComments.styles';

interface DocumentCommentItemProps {
  comments: DocumentComment[];
  document: APIDocument;
  listProps?: { height?: number; onScroll?: UIEventHandler<HTMLDivElement> };
}

export const DocumentCommentItems = ({
  comments,
  document,
  listProps
}: DocumentCommentItemProps) => {
  const { currentTheme } = useContext(ThemeContext);
  const { data: currentUser } = useContext(UserContext);
  const { t } = useTranslation();
  const styles = stylesInit(currentTheme);
  const queryClient = useQueryClient();
  const [form] = useForm();

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editText, setEditText] = useState('');

  const invalidateComments = () => {
    queryClient.invalidateQueries(documentsQueries.getById(document.id).queryKey);
    queryClient.invalidateQueries(documentsQueries.getComments(document.id).queryKey);
    // Also refresh the documents list so the row comment indicator (comments_count /
    // last_comments come from the list endpoint) reflects the change.
    queryClient.invalidateQueries(documentsQueries.getList().queryKey);
  };

  const { mutate } = useMutation(documents.postComment, {
    onSuccess: () => {
      form.resetFields();
      invalidateComments();
    },
    onError: (error: AxiosError) => {
      form.setFields(formErrorHandler(error));
    }
  });

  const { mutate: patchComment, isLoading: isSavingEdit } = useMutation(
    (variables: { id: number; text: string }) =>
      documents.patchComment(variables.id, { text: variables.text }),
    {
      onSuccess: () => {
        setEditingId(null);
        invalidateComments();
      }
    }
  );

  const { mutate: removeComment } = useMutation(
    (id: number) => documents.deleteComment(id),
    { onSuccess: invalidateComments }
  );

  const onSubmit = (value: { text: string }) => {
    mutate({
      ...value,
      thread_id: comments[comments.length - 1]?.thread_id,
      documentId: document.id,
      response: comments[comments.length - 1]?.response
    });
  };

  // A comment is editable/deletable only by its own portal author; Apollo comments never are.
  const canModify = (comment: DocumentComment) =>
    comment.source !== DocumentCommentSource.APOLLO &&
    !!currentUser?.id &&
    comment.user?.id === currentUser.id;

  const startEditing = (comment: DocumentComment) => {
    setEditingId(comment.id);
    setEditText(comment.text ?? '');
  };

  return (
    <div>
      <div
        css={styles.CommentList}
        style={listProps?.height ? { maxHeight: listProps.height, overflowY: 'auto' } : undefined}
        onScroll={listProps?.onScroll}
      >
        {comments.map((comment) => (
          <div css={styles.CommentItem} key={comment.id}>
            <div css={styles.CommentItemHeader}>
              <div css={styles.CommentItemAuthor}>
                <span>
                  {comment.source === DocumentCommentSource.APOLLO
                    ? comment.source
                    : comment.user?.name}
                </span>{' '}
                raspuns
              </div>
              {canModify(comment) && editingId !== comment.id && (
                <div css={styles.CommentItemActions}>
                  <button
                    type="button"
                    className="edit"
                    aria-label={t('edit')}
                    onClick={() => startEditing(comment)}
                  >
                    <Edit />
                  </button>
                  <Popconfirm
                    title={t('deleteCommentConfirm')}
                    okText={t('confirm')}
                    cancelText={t('cancel')}
                    onConfirm={() => removeComment(comment.id)}
                  >
                    <button type="button" className="danger" aria-label={t('delete')}>
                      <TrashFull />
                    </button>
                  </Popconfirm>
                </div>
              )}
            </div>

            <div css={styles.CommentItemDate}>
              {format(comment.created_at, dateSpecialFormat)} ora{' '}
              {format(comment.created_at, timeSpecialFormat)}
            </div>

            {editingId === comment.id ? (
              <div css={styles.CommentItemEdit}>
                <Input.TextArea
                  autoSize={{ minRows: 1, maxRows: 6 }}
                  value={editText}
                  onChange={(event) => setEditText(event.target.value)}
                />
                <div css={styles.CommentItemEditButtons}>
                  <Button size="small" onClick={() => setEditingId(null)}>
                    {t('cancel')}
                  </Button>
                  <Button
                    size="small"
                    type="primary"
                    loading={isSavingEdit}
                    disabled={!editText.trim()}
                    onClick={() => patchComment({ id: comment.id, text: editText.trim() })}
                  >
                    {t('save')}
                  </Button>
                </div>
              </div>
            ) : (
              <div css={styles.CommentItemText}>{comment.text}</div>
            )}
          </div>
        ))}
      </div>
      <Form form={form} css={styles.CommentItemForm} onFinish={onSubmit}>
        <Form.Item
          name="text"
          extra={
            <Button
              css={styles.CommentItemFormButton}
              type="ghost"
              size="small"
              htmlType="submit"
              icon={<Campaigns />}
            />
          }
          rules={[
            {
              required: true,
              message: 'Te rugam introdu un mesaj!'
            }
          ]}
        >
          <Input.TextArea autoSize={{ minRows: 1, maxRows: 6 }} placeholder="Mesajul tau" />
        </Form.Item>
      </Form>
    </div>
  );
};
